/**
 * One tick of gamepad input (08 §3): `readPad`'s intent plus what needs memory - the press
 * edges (bumpers, B, A, L3, R3, Start) - and the right stick turned into an aim. Pure: feed it
 * `PadState`s. The camera is fixed north-up, so stick right is east and stick down is south.
 */

import { GRENADE } from '../sim/tuning';
import { AIM_DEAD_ZONE, type PadIntent, type PadState, readPad } from './gamepadMap';

/** The right stick's throw, from the edge of its dead zone to the rim, sets the aim distance. */
export const PAD_AIM_MIN = 4;
/**
 * The rim of the stick is the grenade's own range, scaled by the thrower's arm. R6: this was a
 * hand-kept 20, two metres SHORT of the base 22, so a pad could not reach the standard throw at
 * all and Long Arm's extra quarter was unreachable on every scheme (`latch.ts` says the rest).
 */
export const PAD_AIM_MAX = GRENADE.maxRange;

/** A stick has to move this much between ticks to count as being touched. */
const STICK_TOUCH = 0.02;

export interface PadFrame {
  /**
   * The pad was touched this tick - a button changed or a stick moved - so it is the device in
   * use. A CHANGE, not a state: a stick that rests off centre must not take the aim from the mouse.
   */
  active: boolean;
  moveX: number;
  moveZ: number;
  /** Radians (0 = south) while the right stick is deflected; null when it is centred. */
  aim: number | null;
  /** Metres, `PAD_AIM_MIN`..`PAD_AIM_MAX`; null with `aim`. */
  aimDist: number | null;
  fire: boolean;
  use: boolean;
  interact: boolean;
  reload: boolean;
  firstAid: boolean;
  /** D-pad down, held: the caller times the 0.6 s. */
  recall: boolean;
  map: boolean;
  run: boolean;
  crawl: boolean;
  sneak: boolean;
  runPressed: boolean;
  crawlPressed: boolean;
  sneakPressed: boolean;
  knifePressed: boolean;
  /** +1 = RB pressed (next weapon), -1 = LB, 0 = neither or both. */
  cycle: number;
}

const IDLE: PadFrame = {
  active: false, moveX: 0, moveZ: 0, aim: null, aimDist: null,
  fire: false, use: false, interact: false, reload: false, firstAid: false, recall: false, map: false,
  run: false, crawl: false, sneak: false, runPressed: false, crawlPressed: false, sneakPressed: false,
  knifePressed: false, cycle: 0,
};

/** Did anything on the pad change between two reads? `before` null = the pad has just appeared. */
function touched(before: PadIntent | null, now: PadIntent): boolean {
  for (const key of Object.keys(now) as Array<keyof PadIntent>) {
    const a = before === null ? 0 : before[key];
    const b = now[key];
    if (typeof b === 'number' ? Math.abs(b - Number(a)) > STICK_TOUCH : b !== Boolean(a)) return true;
  }
  return false;
}

export class PadReader {
  private prev: PadIntent | null = null;
  private prevPause = false;

  /** Once per sim tick; null = no pad connected. `reach` is the thrower's `SimMods.grenadeRange`. */
  read(state: PadState | null, reach = 1): PadFrame {
    if (state === null) {
      this.prev = null;
      return IDLE;
    }
    const now = readPad(state);
    const before = this.prev;
    this.prev = now;
    const edge = (pick: (i: PadIntent) => boolean): boolean => pick(now) && !(before !== null && pick(before));

    let aim: number | null = null;
    let aimDist: number | null = null;
    if (now.aimX !== 0 || now.aimZ !== 0) {
      const throwLen = Math.min(1, Math.hypot(state.axes[2] ?? 0, state.axes[3] ?? 0));
      const t = Math.max(0, (throwLen - AIM_DEAD_ZONE) / (1 - AIM_DEAD_ZONE));
      aim = Math.atan2(now.aimX, now.aimZ);
      aimDist = PAD_AIM_MIN + (PAD_AIM_MAX * reach - PAD_AIM_MIN) * t;
    }
    return {
      active: touched(before, now),
      moveX: now.moveX,
      moveZ: now.moveZ,
      aim,
      aimDist,
      fire: now.fire,
      use: now.use,
      interact: now.interact,
      reload: now.reload,
      firstAid: now.firstAid,
      recall: now.recall,
      map: now.map,
      run: now.run,
      crawl: now.crawl,
      sneak: now.sneak,
      runPressed: edge((i) => i.run),
      crawlPressed: edge((i) => i.crawl),
      sneakPressed: edge((i) => i.sneak),
      knifePressed: edge((i) => i.knife),
      cycle: (edge((i) => i.nextWeapon) ? 1 : 0) - (edge((i) => i.prevWeapon) ? 1 : 0),
    };
  }

  /**
   * Start, on its press edge. Kept apart from `read` because the app asks about pause every
   * frame, paused or not, while `read` only runs with the sim.
   */
  pausePressed(state: PadState | null): boolean {
    const down = state !== null && readPad(state).pause;
    const pressed = down && !this.prevPause;
    this.prevPause = down;
    return pressed;
  }
}
